import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { FileInput } from '../components/ui/FileInput';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import {
  RUN_LEASE_WAIT_MS,
  RUN_MAX_BUSY_WAITS,
  RUN_RETRY_DELAYS_MS,
  classifyRunError,
  sleepWithCountdown,
} from '../lib/importRun';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from '../components/StepUpCodeField';
import {
  ApiError,
  type AddImportJobFileResponse,
  type ImportAttributeReport,
  type ImportJob,
  type ImportJobKind,
  type ImportJobListResponse,
  type MailingList,
  type RunImportJobBatchResponse,
} from '../types/api';

/** `draft`/`running` are the only cancellable states — `done`/`failed`/`cancelled` have nothing left to purge. */
function isCancellable(status: ImportJob['status']): boolean {
  return status === 'draft' || status === 'running';
}

const KIND_LABEL: Record<ImportJobKind, string> = {
  mailchimp_import: 'Full import (subscribed/unsubscribed/cleaned)',
  mailchimp_unsubscribe_sync: 'Unsubscribe sync (delta before cutover)',
};

const STATUS_LABEL: Record<ImportJob['status'], string> = {
  draft: 'Draft',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsText(file);
  });
}

export function importJobErrorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (isTotpLocked(error)) return TOTP_LOCKED_MESSAGE;
  if (error instanceof ApiError && error.status === 403) {
    return 'Only administrators can import from Mailchimp.';
  }
  if (isStepUpRequired(error)) return STEP_UP_ENDED_MESSAGE;
  if (error instanceof ApiError && error.status === 401) return 'The code is incorrect.';
  if (error instanceof ApiError && error.body.message) return error.body.message;
  return fallback;
}

const ATTRIBUTE_COLUMN_LABEL: [keyof ImportAttributeReport['columns']['recognised'], string][] = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['language', 'Language'],
  ['country', 'Country'],
];

/**
 * Which of the file's columns fed a contact's name, language and country,
 * and which were ignored. Column headers only (the file's layout), never a
 * value from a row.
 */
function AttributeColumns({ attributes }: { attributes: ImportAttributeReport }) {
  const { recognised, ignored, ignoredValues, profile } = attributes.columns;
  return (
    <div data-testid="import-columns">
      <h3 className="text-xs font-semibold text-muted m-0 mb-1">
        Name, language, country, place and Mailchimp columns
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs max-w-[520px]">
        {ATTRIBUTE_COLUMN_LABEL.map(([key, label]) => (
          <div key={key} className="contents">
            <dt className="text-muted">{label}</dt>
            <dd className="m-0 text-right">
              {recognised[key].length > 0 ? recognised[key].join(', ') : 'No column found'}
            </dd>
          </div>
        ))}
        <dt className="text-muted">City, postal code, region, time zone, Mailchimp data</dt>
        <dd className="m-0 text-right">
          {profile.length > 0 ? profile.join(', ') : 'No column found'}
        </dd>
        <dt className="text-muted">Ignored columns</dt>
        <dd className="m-0 text-right">
          {ignored.length > 0
            ? ignored.map((name) => `${name} (${ignoredValues[name] ?? 0} values)`).join(', ')
            : 'None'}
        </dd>
      </dl>
    </div>
  );
}

/** Flat, counts-only summary — every field on `ImportReport`/`UnsubscribeSyncReport` is a number or a nested object of numbers, never a row of contact data. */
function ReportSummary({ report }: { report: ImportJob['report'] }) {
  const rows: [string, number][] = [];
  function walk(prefix: string, value: unknown): void {
    if (typeof value === 'number') {
      rows.push([prefix, value]);
    } else if (value && typeof value === 'object') {
      for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
        if (key === 'columns') continue; // shown by AttributeColumns
        walk(prefix ? `${prefix} · ${key}` : key, v);
      }
    }
  }
  walk('', report);

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs max-w-[520px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted">{label}</dt>
          <dd className="m-0 text-right font-mono">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Settings → Mailchimp import (P7). Admin-only: create a job, upload
 * Mailchimp export files (CSV text, never stored locally by the page
 * itself), start it (requires a fresh 2FA code unless it's a dry run) and
 * run it in chunks until it's done. Everything shown is counts only —
 * never an email address, a name or an IP.
 */
export interface MailchimpImportPageProps {
  /** Test seam: pauses before retry 1..n (defaults to 2, 5, 10, 20, 30 s). */
  retryDelaysMs?: readonly number[];
  /** Test seam: how long to wait when the server says the job is busy (default 60 s). */
  leaseWaitMs?: number;
}

/** What the browser-driven run loop is doing right now. */
type RunState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'retrying'; attempt: number; max: number; secondsLeft: number }
  | { phase: 'busy'; secondsLeft: number }
  | { phase: 'stopped'; message: string };

export function MailchimpImportPage({
  retryDelaysMs = RUN_RETRY_DELAYS_MS,
  leaseWaitMs = RUN_LEASE_WAIT_MS,
}: MailchimpImportPageProps = {}) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ImportJobKind>('mailchimp_import');
  const [chosenListId, setChosenListId] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [cancelTotpCode, setCancelTotpCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const kindId = useId();
  const listSelectId = useId();
  const stepUp = useStepUpWindow();
  const totpId = useId();
  const cancelTotpId = useId();

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['mailchimp-import', 'jobs'],
    queryFn: () => apiFetch<ImportJobListResponse>('/mailchimp-import/jobs?limit=20'),
  });

  // Same query and key as AudiencePicker, so the cache is shared. Archived
  // lists are left out by the API.
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => apiFetch<{ items: MailingList[] }>('/lists'),
  });
  const targetLists = (lists.data?.items ?? []).filter((l) => !l.archived);
  // Defaults to the list a bare signup lands on, else the first list; no id is ever hard-coded.
  const listId =
    chosenListId || (targetLists.find((l) => l.isDefaultSignup) ?? targetLists[0])?.id || '';
  // Only a full import creates contacts, so only it needs a list.
  const needsList = kind === 'mailchimp_import';

  const { data: activeJob } = useQuery({
    queryKey: ['mailchimp-import', 'job', activeJobId],
    queryFn: () => apiFetch<ImportJob>(`/mailchimp-import/jobs/${activeJobId}`),
    enabled: activeJobId !== null,
    refetchInterval: (query) =>
      query.state.data && query.state.data.status === 'running' ? 1000 : false,
  });

  // Returns the invalidation's promise (React Query resolves it once every
  // matching *active* query has refetched) so mutation `onSuccess` handlers
  // can `await`/`return` it — otherwise the fire-and-forget `void` call lets
  // React Query batch the invalidation past the point where a caller (or a
  // test) can observe the refetch actually landing.
  //
  // `cancelQueries` first is load-bearing, not decorative: these mutations
  // fire in quick succession (add file → start → run), and each one's own
  // `enabled`/polling queries can still have a *previous* GET in flight when
  // the next mutation's `onSuccess` calls `invalidateAll`. Without
  // cancelling it first, React Query dedupes the invalidated refetch into
  // that already-in-flight request — which was sent *before* this
  // mutation's write landed — so the UI would silently keep showing
  // pre-mutation data (e.g. the job stuck on "Kladde" after Start
  // succeeded) even though `invalidateQueries` "ran".
  const invalidateAll = async () => {
    await queryClient.cancelQueries({ queryKey: ['mailchimp-import'] });
    await queryClient.invalidateQueries({ queryKey: ['mailchimp-import'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<ImportJob>('/mailchimp-import/jobs', {
        method: 'POST',
        body: needsList ? { kind, dryRun, listId } : { kind, dryRun },
      }),
    onSuccess: (job) => {
      setActiveJobId(job.id);
      setNotice(`The job has been created (${dryRun ? 'dry run' : 'real run'}).`);
      return invalidateAll();
    },
  });

  const addFileMutation = useMutation({
    mutationFn: ({ fileName, csvText }: { fileName: string; csvText: string }) =>
      apiFetch<AddImportJobFileResponse>(`/mailchimp-import/jobs/${activeJobId}/files`, {
        method: 'POST',
        body: { fileName, csvText },
      }),
    onSuccess: (result, { fileName }) => {
      setNotice(`"${fileName}" added: ${result.rowsAdded} rows in ${result.chunksAdded} chunks.`);
      return invalidateAll();
    },
  });

  const job = activeJob ?? null;
  // The 2FA gate follows the job's own mode, not the create-form checkbox:
  // the checkbox can be toggled after the job exists.
  const jobIsDryRun = job?.dryRun ?? true;

  const startMutation = useMutation({
    mutationFn: () =>
      apiFetch<ImportJob>(`/mailchimp-import/jobs/${activeJobId}/start`, {
        method: 'POST',
        body: jobIsDryRun || stepUp.active ? {} : stepUpBody(totpCode),
      }),
    onSuccess: () => {
      if (!jobIsDryRun && !stepUp.active) stepUp.onStepUpSuccess();
      setTotpCode('');
      setNotice('The job has been started.');
      return invalidateAll();
    },
    onError: stepUp.onStepUpError,
  });

  const [runState, setRunState] = useState<RunState>({ phase: 'idle' });
  // The browser drives the job, so this guard is what keeps a retry (or a
  // double click) from ever running two batches at once from this tab. A
  // loop counts as active until it is aborted — not until its in-flight
  // request returns — so opening another job can start a run right away.
  const runAbort = useRef<AbortController | null>(null);
  const runInProgress =
    runState.phase === 'running' || runState.phase === 'retrying' || runState.phase === 'busy';

  const runLoop = useCallback(
    async (jobId: string) => {
      if (runAbort.current && !runAbort.current.signal.aborted) return;
      const controller = new AbortController();
      runAbort.current = controller;
      const { signal } = controller;
      let failures = 0;
      let busyWaits = 0;
      try {
        while (!signal.aborted) {
          setRunState({ phase: 'running' });
          try {
            const result = await apiFetch<RunImportJobBatchResponse>(
              `/mailchimp-import/jobs/${jobId}/run`,
              { method: 'POST', body: {}, signal },
            );
            failures = 0;
            busyWaits = 0;
            if (signal.aborted) return;
            await invalidateAll();
            if (result.done) {
              setNotice('The job is done.');
              setRunState({ phase: 'idle' });
              return;
            }
            // More chunks remain — keep going without a fresh 2FA code (only
            // `start` is step-up gated, see apps/app/src/routes/mailchimpImport.ts).
          } catch (err) {
            if (signal.aborted) return;
            const kind = classifyRunError(err);
            if (kind === 'fatal') {
              setRunState({
                phase: 'stopped',
                message: importJobErrorMessage(err, 'The run failed.') ?? 'The run failed.',
              });
              void invalidateAll();
              return;
            }
            if (kind === 'busy') {
              busyWaits += 1;
              if (busyWaits > RUN_MAX_BUSY_WAITS) {
                setRunState({
                  phase: 'stopped',
                  message:
                    'The job is still being processed by another request. Wait a minute, then click Run job.',
                });
                return;
              }
              const completed = await sleepWithCountdown(leaseWaitMs, signal, (secondsLeft) =>
                setRunState({ phase: 'busy', secondsLeft }),
              );
              if (!completed) return;
              continue;
            }
            failures += 1;
            if (failures > retryDelaysMs.length) {
              setRunState({
                phase: 'stopped',
                message: `${importJobErrorMessage(err, 'The run failed.') ?? 'The run failed.'} Gave up after ${retryDelaysMs.length} retries.`,
              });
              return;
            }
            const attempt = failures;
            const completed = await sleepWithCountdown(
              retryDelaysMs[attempt - 1] ?? 30_000,
              signal,
              (secondsLeft) =>
                setRunState({
                  phase: 'retrying',
                  attempt,
                  max: retryDelaysMs.length,
                  secondsLeft,
                }),
            );
            if (!completed) return;
          }
        }
      } finally {
        if (runAbort.current === controller) runAbort.current = null;
      }
    },
    [queryClient, retryDelaysMs, leaseWaitMs],
  );

  // Stop the loop when another job is opened or the page is left.
  useEffect(() => {
    return () => {
      runAbort.current?.abort();
      setRunState({ phase: 'idle' });
    };
  }, [activeJobId]);

  // The browser drives the job: warn before the tab is closed or reloaded.
  useEffect(() => {
    if (!runInProgress) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [runInProgress]);

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<ImportJob>(`/mailchimp-import/jobs/${activeJobId}/cancel`, {
        method: 'POST',
        body: stepUp.active ? {} : stepUpBody(cancelTotpCode),
      }),
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      setCancelTotpCode('');
      setNotice('The job has been cancelled, and the remaining data has been deleted.');
      return invalidateAll();
    },
    onError: stepUp.onStepUpError,
  });

  async function handleFiles(fileList: FileList | null): Promise<void> {
    if (!fileList || activeJobId === null) return;
    for (const file of Array.from(fileList)) {
      const csvText = await readFileAsText(file);
      await addFileMutation.mutateAsync({ fileName: file.name, csvText });
    }
  }

  const createError = importJobErrorMessage(createMutation.error, 'The job could not be created.');
  const fileError = importJobErrorMessage(addFileMutation.error, 'The file could not be uploaded.');
  const startError = importJobErrorMessage(startMutation.error, 'The job could not be started.');
  const cancelError = importJobErrorMessage(
    cancelMutation.error,
    'The job could not be cancelled.',
  );

  const canStart = job?.status === 'draft' && job.totalChunks > 0;
  const canRun = job?.status === 'running';
  const canCancel = job !== null && isCancellable(job.status);
  const startDisabled =
    startMutation.isPending ||
    !canStart ||
    (!jobIsDryRun && !stepUp.active && !stepUpCodeReady(totpCode));
  const cancelDisabled =
    cancelMutation.isPending || !canCancel || !(stepUp.active || stepUpCodeReady(cancelTotpCode));
  const createDisabled = createMutation.isPending || (needsList && !listId);
  const jobListName =
    !job || job.listId === null
      ? 'No list'
      : (lists.data?.items.find((l) => l.id === job.listId)?.name ??
        (lists.data ? 'Archived list' : '…'));

  if (!isAdmin) {
    return (
      <>
        <header className="flex items-center gap-3">
          <h1 className="m-0 text-2xl font-semibold tracking-tight">Mailchimp import</h1>
          <MockBadge />
        </header>
        <Alert>Only administrators can import from Mailchimp.</Alert>
      </>
    );
  }

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Mailchimp import</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted max-w-[640px]">
        Upload Mailchimp's standard export files (subscribed, unsubscribed, cleaned). Always run a
        dry run first — it writes nothing, only counts. A real run requires a code from the
        authenticator app to start, unless you entered one in the last 30 minutes.
      </p>

      <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
        <h2 className="text-sm font-semibold m-0">New job</h2>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={kindId}>Type</Label>
          <select
            id={kindId}
            className="h-[38px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as ImportJobKind)}
          >
            <option value="mailchimp_import">{KIND_LABEL.mailchimp_import}</option>
            <option value="mailchimp_unsubscribe_sync">
              {KIND_LABEL.mailchimp_unsubscribe_sync}
            </option>
          </select>
        </div>
        {needsList ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={listSelectId}>Add contacts to list</Label>
            <select
              id={listSelectId}
              className="h-[38px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
              value={listId}
              required
              disabled={targetLists.length === 0}
              onChange={(e) => setChosenListId(e.target.value)}
            >
              {targetLists.length === 0 ? (
                <option value="">{lists.isLoading ? 'Loading lists…' : 'No lists'}</option>
              ) : null}
              {targetLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            {lists.isError ? (
              <p role="alert" className="m-0 text-xs text-danger-text">
                The lists could not be loaded.
              </p>
            ) : lists.isSuccess && targetLists.length === 0 ? (
              <p className="m-0 text-xs text-muted">
                There are no lists yet. Create one under{' '}
                <Link to="/indstillinger/lister">Lists</Link> first.
              </p>
            ) : (
              <p className="m-0 text-xs text-muted">
                New contacts join this list. Campaigns are sent to lists.
              </p>
            )}
          </div>
        ) : null}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          Dry run (writes nothing)
        </label>
        {createError ? <Alert>{createError}</Alert> : null}
        <Button
          type="button"
          className="self-end"
          disabled={createDisabled}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isPending ? 'Creating…' : 'Create job'}
        </Button>
      </Card>

      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}

      {job ? (
        <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
          <h2 className="text-sm font-semibold m-0">
            Job {job.id} — {STATUS_LABEL[job.status]}
          </h2>
          <div className="text-xs text-muted">
            {KIND_LABEL[job.kind]} · {job.dryRun ? 'dry run' : 'real run'} · created{' '}
            {formatDateTime(job.createdAt)}
          </div>
          {job.kind === 'mailchimp_import' ? (
            <div className="text-xs text-muted">List: {jobListName}</div>
          ) : null}

          {job.status === 'draft' ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="mailchimp-import-files">Add export file(s) (CSV)</Label>
              <FileInput
                id="mailchimp-import-files"
                accept=".csv,text/csv"
                multiple
                disabled={addFileMutation.isPending}
                onChange={(e) => {
                  void handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              {fileError ? <Alert>{fileError}</Alert> : null}
              <div className="text-xs text-muted">
                {job.totalChunks} chunk(s), {job.totalRows} row(s) uploaded so far.
              </div>
            </div>
          ) : null}

          {job.status === 'draft' ? (
            <div className="flex flex-col gap-2">
              {!jobIsDryRun ? (
                <StepUpCodeField
                  id={totpId}
                  value={totpCode}
                  onChange={setTotpCode}
                  active={stepUp.active}
                  minutesLeft={stepUp.minutesLeft}
                  inputClassName="max-w-[200px]"
                />
              ) : null}
              {startError ? <Alert>{startError}</Alert> : null}
              <Button
                type="button"
                className="self-end"
                disabled={startDisabled}
                onClick={() => startMutation.mutate()}
              >
                {startMutation.isPending ? 'Starting…' : 'Start job'}
              </Button>
            </div>
          ) : null}

          {canRun ? (
            <div className="flex flex-col gap-2">
              {runState.phase === 'stopped' ? <Alert>{runState.message}</Alert> : null}
              <div className="text-xs text-muted">
                {job.processedChunks} of {job.totalChunks} chunk(s) processed.
              </div>
              {runInProgress ? (
                <div role="status" className="text-xs">
                  {runState.phase === 'retrying'
                    ? `Connection lost — retrying in ${runState.secondsLeft} s (attempt ${runState.attempt} of ${runState.max})`
                    : runState.phase === 'busy'
                      ? `The server is still finishing the previous batch — trying again in ${runState.secondsLeft} s`
                      : `Running… (${job.processedChunks} of ${job.totalChunks} chunks)`}
                </div>
              ) : null}
              {runInProgress ? (
                <div className="text-xs text-muted">
                  Keep this tab open — the import runs from this page.
                </div>
              ) : null}
              <Button
                type="button"
                className="self-end"
                disabled={runInProgress}
                onClick={() => {
                  if (activeJobId) void runLoop(activeJobId);
                }}
              >
                {runInProgress ? 'Running…' : 'Run job'}
              </Button>
            </div>
          ) : null}

          {canCancel ? (
            <div className="flex flex-col gap-2 border-t border-zinc-200 pt-3">
              <StepUpCodeField
                id={cancelTotpId}
                value={cancelTotpCode}
                onChange={setCancelTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
                label="Code from the authenticator app (to cancel)"
                inputClassName="max-w-[200px]"
              />
              {cancelError ? <Alert>{cancelError}</Alert> : null}
              <Button
                type="button"
                variant="danger"
                className="self-end"
                disabled={cancelDisabled}
                onClick={() => cancelMutation.mutate()}
              >
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel job and delete remaining data'}
              </Button>
            </div>
          ) : null}

          <div>
            <h3 className="text-xs font-semibold text-muted m-0 mb-1">Report (counts only)</h3>
            <ReportSummary report={job.report} />
          </div>
          {'attributes' in job.report ? (
            <AttributeColumns attributes={job.report.attributes} />
          ) : null}
          {job.error ? <Alert>{job.error}</Alert> : null}
        </Card>
      ) : null}

      <Card className="p-4 flex flex-col gap-2 max-w-[640px]">
        <h2 className="text-sm font-semibold m-0">Previous jobs</h2>
        {listLoading ? <div className="text-muted text-sm">Loading…</div> : null}
        {listData && listData.items.length === 0 ? (
          <p className="m-0 text-sm text-muted">No jobs yet.</p>
        ) : null}
        {listData && listData.items.length > 0 ? (
          <ul className="m-0 p-0 list-none flex flex-col gap-1 text-sm">
            {listData.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="text-link underline-offset-2 hover:underline"
                  onClick={() => setActiveJobId(item.id)}
                >
                  {KIND_LABEL[item.kind]} — {STATUS_LABEL[item.status]}
                </button>{' '}
                <span className="text-xs text-muted">
                  ({item.dryRun ? 'dry run' : 'real'}, {formatDateTime(item.createdAt)})
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    </>
  );
}
